import { useI18n } from "@/i18n/I18nContext";
import { MantraAudioIcon } from "@/components/audio/MantraAudioIcon";
import { hasDevanagari, resolveMantraId, isBijaMantra } from "@/lib/audio/mantra-registry";
import { MeaningAudioIcon } from "@/components/audio/MeaningAudioIcon";
import {
  hasSanskritContent,
  normalizeTriLanguageItem,
  resolveTriLanguage,
  getLanguageLabel,
  mapSlideToTriItem,
  getPreferredMeaning,
  getEnglishMeaning,
} from "@/lib/i18n/tri-language";

export { getPreferredMeaning, getEnglishMeaning };

function MeaningRowHeader({ label, text, language, mode, token, title, fallbackText }) {
  if (!text) return <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{label}</p>;
  return (
    <div className="flex items-center gap-2 mb-1">
      <p className="text-xs uppercase tracking-wide text-muted-foreground flex-1">{label}</p>
      <MeaningAudioIcon
        text={text}
        language={language}
        mode={mode}
        token={token}
        title={title}
        size={14}
        fallbackLanguage="en"
        fallbackText={fallbackText}
      />
    </div>
  );
}

export function SanskritDevanagari({ text, className = "", suffix = null }) {
  if (!text) return null;
  return (
    <p
      className={`font-sanskrit text-primary leading-relaxed ${className}`}
      data-testid="tri-sanskrit-devanagari"
    >
      <span className="inline-flex items-center gap-1 flex-wrap">
        <span>{text}</span>
        {suffix}
      </span>
    </p>
  );
}

export function SanskritIAST({ text, className = "" }) {
  if (!text) return null;
  return (
    <p className={`text-xs italic text-muted-foreground ${className}`} data-testid="tri-sanskrit-iast">
      {text}
    </p>
  );
}

export function EnglishTranslation({ text, label, className = "", meaningAudioMode = "meaning", token, showMeaningAudio = true }) {
  const { triSettings, t } = useI18n();
  if (!text || triSettings?.showEnglish === false) return null;
  return (
    <div className={className} data-testid="tri-english">
      {showMeaningAudio ? (
        <MeaningRowHeader
          label={label || t("tri.english") || "English"}
          text={text}
          language="en"
          mode={meaningAudioMode}
          token={token}
          title={t("btn.listen") || "Listen to meaning"}
        />
      ) : (
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
          {label || t("tri.english") || "English"}
        </p>
      )}
      <p className="text-foreground/90">{text}</p>
    </div>
  );
}

export function PreferredLanguageTranslation({
  text,
  lang,
  label,
  className = "",
  meaningAudioMode = "meaning",
  token,
  showMeaningAudio = true,
  englishFallbackText,
}) {
  const { lang: preferred, t } = useI18n();
  const code = lang || preferred;
  if (!text || code === "en") return null;
  return (
    <div className={className} data-testid="tri-preferred">
      {showMeaningAudio ? (
        <MeaningRowHeader
          label={label || getLanguageLabel(code) || t("tri.preferred") || "Preferred"}
          text={text}
          language={code}
          mode={meaningAudioMode}
          token={token}
          title={t("btn.listen") || "Listen to meaning"}
          fallbackText={englishFallbackText}
        />
      ) : (
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
          {label || getLanguageLabel(code) || t("tri.preferred") || "Preferred"}
        </p>
      )}
      <p className="text-muted-foreground">{text}</p>
    </div>
  );
}

/**
 * Phase 8–9 — renders Sanskrit → IAST → English → preferred (universal order).
 */
export function TriLanguageBlock({
  item,
  meaningPrefix = "translation",
  englishLabel,
  preferredLabel,
  compact = false,
  className = "",
  hideSanskrit = false,
  meaningAudioMode = "meaning",
  showMeaningAudio = true,
  token,
}) {
  const { lang, triSettings } = useI18n();
  const normalized = normalizeTriLanguageItem(item);
  const tri = resolveTriLanguage(normalized, lang, { meaningPrefix });

  if (!tri.sanskrit_devanagari && !tri.sanskrit_iast && !tri.english && !tri.preferred) {
    return null;
  }

  const showSanskrit = triSettings?.showSanskrit !== false && !hideSanskrit;
  const preferredText =
    tri.preferredLang !== "en" && tri.preferred ? tri.preferred : "";

  return (
    <div className={`space-y-2 ${className}`} data-testid="tri-language-block">
      {showSanskrit && (
        <>
          <SanskritDevanagari
            text={tri.sanskrit_devanagari}
            className={compact ? "text-base" : "text-lg"}
          />
          <SanskritIAST text={tri.sanskrit_iast} />
        </>
      )}
      <EnglishTranslation
        text={tri.english}
        label={englishLabel}
        meaningAudioMode={meaningAudioMode}
        showMeaningAudio={showMeaningAudio}
        token={token}
      />
      <PreferredLanguageTranslation
        text={preferredText}
        lang={tri.preferredLang}
        label={preferredLabel}
        meaningAudioMode={meaningAudioMode}
        showMeaningAudio={showMeaningAudio}
        token={token}
        englishFallbackText={tri.english}
      />
    </div>
  );
}

/** Renders tri-language for mantra lines with optional Sādhanā audio icon. */
export function MantraTriLanguageBlock({
  item,
  token,
  meaningPrefix = "translation",
  showAudio = true,
  compact = false,
  className = "",
  textClassName = "",
}) {
  const { triSettings } = useI18n();
  const normalized = normalizeTriLanguageItem(item);
  if (!hasSanskritContent(normalized)) return null;

  const deva = normalized.sanskrit_devanagari || "";
  const mantraId = resolveMantraId(deva);
  const bija = isBijaMantra(deva);
  const audio =
    showAudio && hasDevanagari(deva) ? (
      <MantraAudioIcon mantra={mantraId ?? deva} token={token} title="Play mantra" size={18} />
    ) : null;

  return (
    <div
      className={`mantra-line flex flex-col items-start gap-1 ${className}`.trim()}
      data-testid="mantra-tri-language"
    >
      {triSettings?.showSanskrit !== false && deva && (
        <p
          className={`font-sanskrit text-primary leading-relaxed inline-flex items-center gap-1 flex-wrap ${textClassName}`.trim()}
          data-testid="tri-sanskrit-devanagari"
        >
          <span>{deva}</span>
          {audio}
        </p>
      )}
      <TriLanguageBlock
        item={normalized}
        meaningPrefix={meaningPrefix}
        compact={compact}
        hideSanskrit
        className="w-full"
        meaningAudioMode="meaning"
        showMeaningAudio
        token={token}
      />
      {bija && (
        <span className="text-xs text-amber-800/90" data-testid="bija-guru-notice">
          For this mantra, seek guidance from a living Guru.
        </span>
      )}
    </div>
  );
}

/** Only renders TriLanguageBlock when Sanskrit content is detected. */
export function TriLanguageIfSanskrit({ item, children, ...props }) {
  if (!hasSanskritContent(item)) return children ?? null;
  return <TriLanguageBlock item={item} {...props} />;
}

/** Chant slide wrapper — preserves all translation_* / meaning_* fields. */
export function ChantSlideTriLanguage({ slide, className = "", token }) {
  const mapped = mapSlideToTriItem(slide);
  const commentary = slide?.commentary_en || slide?.commentary;
  return (
    <div className={className}>
      <TriLanguageBlock item={mapped} meaningPrefix="meaning" meaningAudioMode="meaning" token={token} />
      {commentary ? (
        <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
          <MeaningRowHeader
            label="Commentary"
            text={commentary}
            language="en"
            mode="commentary"
            token={token}
            title="Listen to commentary"
          />
          <p>{commentary}</p>
        </div>
      ) : null}
    </div>
  );
}
